/**
 * Heidi Work consolidated behavioural event names, and the routing metadata the
 * launch dashboards are built from.
 *
 * THE NAMES ARE NOT DECLARED HERE ANY MORE (D9 ruling 27, ARD-1455). Each one
 * is declared exactly once, by its owning domain, in
 * `packages/agents/src/contributions/analytics.ts` — this map reads `.name` off
 * those descriptors rather than re-typing the literal, so the app-side map and
 * the domain declaration cannot drift, and the event-reference gate's third arm
 * sees every name from the one place it is written.
 *
 * The `{ side, dashboard }` metadata below stays app-side on purpose: it is
 * routing/reporting vocabulary rather than the event's identity, and the shell
 * descriptor deliberately types only what every kind of consumer needs.
 *
 * This module carried a `.generated` suffix until ruling 27, and the suffix was
 * never true: no generator has ever written the file, and its own first line
 * said "hand-maintained". That name is a standing instruction — do not edit by
 * hand, go find the script that owns this — and both halves were wrong here.
 * It is dropped now that the names really do come from somewhere else, so the
 * one thing this module IS derived from is the import list below rather than a
 * word in its filename.
 */
import {
  workApprovalActioned,
  workArtifactActioned,
  workErrorEncountered,
  workExperienceFeedbackActioned,
  workJobCompleted,
  workMessageActioned,
  workMessageSubmitted,
  workProjectCreated,
  workRunCompleted,
  workRunStarted,
  workSurfaceActioned,
  workTermsActioned,
  workToolCompleted,
  workUpgradeActioned,
  workVoiceHandoffCompleted,
  workWorkflowActioned,
} from '@heidi/agents/contributions';

export const WorkLaunchEventName = {
  WorkSurfaceActioned: workSurfaceActioned.name,
  WorkTermsActioned: workTermsActioned.name,
  WorkProjectCreated: workProjectCreated.name,
  WorkMessageSubmitted: workMessageSubmitted.name,
  WorkRunStarted: workRunStarted.name,
  WorkRunCompleted: workRunCompleted.name,
  WorkToolCompleted: workToolCompleted.name,
  WorkWorkflowActioned: workWorkflowActioned.name,
  WorkArtifactActioned: workArtifactActioned.name,
  WorkApprovalActioned: workApprovalActioned.name,
  WorkJobCompleted: workJobCompleted.name,
  WorkErrorEncountered: workErrorEncountered.name,
  WorkMessageActioned: workMessageActioned.name,
  WorkVoiceHandoffCompleted: workVoiceHandoffCompleted.name,
  WorkExperienceFeedbackActioned: workExperienceFeedbackActioned.name,
  WorkUpgradeActioned: workUpgradeActioned.name,
} as const;

export type WorkLaunchEventName =
  (typeof WorkLaunchEventName)[keyof typeof WorkLaunchEventName];

export type WorkLaunchDashboard =
  | 'activation'
  | 'engagement'
  | 'ux'
  | 'friction'
  | 'conversion'
  | 'retention'
  | 'quality';

export const WORK_LAUNCH_EVENT_META: Record<
  WorkLaunchEventName,
  { side: 'fe' | 'be' | 'both'; dashboard: WorkLaunchDashboard }
> = {
  work_surface_actioned: { side: 'fe', dashboard: 'activation' },
  work_terms_actioned: { side: 'fe', dashboard: 'activation' },
  work_project_created: { side: 'be', dashboard: 'activation' },
  work_message_submitted: { side: 'fe', dashboard: 'engagement' },
  work_run_started: { side: 'be', dashboard: 'engagement' },
  work_run_completed: { side: 'fe', dashboard: 'quality' },
  work_tool_completed: { side: 'be', dashboard: 'quality' },
  work_workflow_actioned: { side: 'fe', dashboard: 'engagement' },
  work_artifact_actioned: { side: 'fe', dashboard: 'engagement' },
  work_approval_actioned: { side: 'fe', dashboard: 'friction' },
  work_job_completed: { side: 'be', dashboard: 'conversion' },
  work_error_encountered: { side: 'both', dashboard: 'friction' },
  work_message_actioned: { side: 'fe', dashboard: 'quality' },
  work_voice_handoff_completed: { side: 'be', dashboard: 'activation' },
  work_experience_feedback_actioned: { side: 'fe', dashboard: 'quality' },
  work_upgrade_actioned: { side: 'fe', dashboard: 'conversion' },
};
